import { FC, lazy } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useSelector } from 'react-redux';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';

import { leadTimeTitle } from '../messages/pdpBlurbMessages';
import { ITEM_LEAD_TIME_AVAILABLE_NOW } from '../../constants/itemConstants';
import { PdpSharedBlurb } from './PdpSharedBlurb/PdpSharedBlurb';

import { PdpSharedBlurbLeadTime_sku$key } from './__generated__/PdpSharedBlurbLeadTime_sku.graphql';
import { PdpSharedBlurbLeadTime_item$key } from './__generated__/PdpSharedBlurbLeadTime_item.graphql';

const ClockIcon = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedBlurbLeadTimeClockIcon" */ 'dibs-icons/exports/legacy/Clock'
        )
);

const skuFragment = graphql`
    fragment PdpSharedBlurbLeadTime_sku on ItemSkuType {
        leadTime(pageType: PDP)
    }
`;

const itemFragment = graphql`
    fragment PdpSharedBlurbLeadTime_item on Item {
        itemType
        isNewAndMadeToOrder
        isListingUnlisted
        attributes {
            leadTime {
                option {
                    name
                    urlLabel
                }
            }
        }
    }
`;

// TODO: replace with State type
type ReduxState = {
    relayVariables: {
        variables: {
            isMultiSku: boolean;
            isClient: boolean;
        };
    };
};

const Icon: FC = () => (
    <ClientSuspense fallback={null}>
        <ClockIcon />
    </ClientSuspense>
);

export const PdpSharedBlurbLeadTime: FC<{
    sku?: PdpSharedBlurbLeadTime_sku$key | null | undefined;
    item: PdpSharedBlurbLeadTime_item$key | null | undefined;
}> = props => {
    const sku = useFragment(skuFragment, props.sku || null);
    const item = useFragment(itemFragment, props.item);

    const { isMultiSku } = useSelector((state: ReduxState) => state.relayVariables.variables);

    const isNewAndMadeToOrder = item?.isNewAndMadeToOrder;
    const isNewAndCustom = item?.itemType === 'NEW_MADE_TO_ORDER';
    const isListingUnlisted = item?.isListingUnlisted;

    const option = item?.attributes?.leadTime?.[0]?.option;
    const { urlLabel: leadTimeUrlLabel, name: leadTimeName } = option || {};
    const isAvailableNow = leadTimeUrlLabel === ITEM_LEAD_TIME_AVAILABLE_NOW;

    let leadTime: string | null | undefined;
    if (
        !isNewAndCustom &&
        !isMultiSku &&
        leadTimeUrlLabel &&
        !isAvailableNow &&
        isNewAndMadeToOrder
    ) {
        leadTime = leadTimeName;
    } else if (isNewAndCustom && !isListingUnlisted) {
        leadTime = sku?.leadTime;
    }

    return leadTime ? (
        <PdpSharedBlurb
            IconComponent={Icon}
            dataTn="pdp-blurb-lead-time"
            title={leadTimeTitle}
            detail={leadTime}
        />
    ) : null;
};
